/**
 * The error taxonomy, as the browser sees it.
 *
 * Every Nexus error arrives in one envelope — `{ error: { code, message,
 * correlation_id, retryable, detail } }` — so the application never has to
 * guess what a failure was from a status code alone. Keeping the code is what
 * lets a screen distinguish "you may not" from "this is not here" from "the
 * database is unreachable", and the correlation id is what lets somebody find
 * the same request in the audit record.
 */

export type ErrorCode =
  | "UNAUTHENTICATED"
  | "POLICY_DENIED"
  | "NOT_FOUND"
  | "CONFLICT"
  | "VALIDATION_FAILED"
  | "DEPENDENCY_UNAVAILABLE"
  | "AUDIT_UNAVAILABLE"
  | "CONFIGURATION_INVALID"
  // Not from the platform's taxonomy: what this application calls a failure
  // that never arrived in the envelope at all.
  | "PLATFORM_ERROR"
  | (string & {});

export interface ErrorBody {
  code: ErrorCode;
  message: string;
  correlation_id?: string | null;
  retryable?: boolean;
  detail?: Record<string, unknown>;
}

/** A failure the platform described. */
export class NexusError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly correlationId: string | null;
  readonly retryable: boolean;
  readonly detail: Record<string, unknown>;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = "NexusError";
    this.status = status;
    this.code = body.code;
    this.correlationId = body.correlation_id ?? null;
    this.retryable = body.retryable ?? false;
    this.detail = body.detail ?? {};
  }

  /** Denied by authority, policy or scope — never a reason to retry. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** No usable session. The shell sends the browser to sign in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** A failure the platform never got to describe — the network, or a proxy. */
export class TransportError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "TransportError";
  }
}
